import { Loader, Result } from 'kiswe-ui'
import { KeyDict } from '@/types'
import type { SceneContent } from '@/types/sceneContent'
import LayoutBox from './LayoutBox'
import { CasterCSS, ChatCSS, LiveChatCSS, SimplecgCSS, TransitionType, type SceneTransition } from '@/types/layouts'
import TwitterStyling from './TwitterStyling'
import { BoxStylingSimpleCg } from './BoxStylingSimpleCg'

export default class Layout {
  id?: string
  name: string = ''
  info: string = ''
  // Layout contents have same interface as scene contents
  contents?: KeyDict<SceneContent>
  boxes: KeyDict<LayoutBox> = {}
  editable?: boolean
  existingLayout?: Omit<Layout, 'existingLayout'>
  transition: SceneTransition = { type: TransitionType.NONE, actions: [] }
  castercss: CasterCSS = new CasterCSS()
  chatcss: ChatCSS = new ChatCSS()
  tweetcss: TwitterStyling = new TwitterStyling()
  livechatcss: LiveChatCSS = new LiveChatCSS()
  /** @deprecated, use styling object inside a LayoutBox instead */
  simplecgcss: KeyDict<SimplecgCSS> = {} //Stores individual box stylings, unlike the others
  clips: { automute: boolean } = { automute: false }
  team_id: string = ''

  // eslint-disable-next-line max-lines-per-function
  constructor (data?: unknown) {
    if (data === undefined) return
    const id = Loader.loadOptionalString(data, 'id')
    if (id !== undefined) this.id = id
    this.name = Loader.loadString(data, 'name', this.name)
    this.info = Loader.loadString(data, 'info', this.info)
    const contents = Loader.loadAndCast<KeyDict<SceneContent>>(data, 'contents', {})
    if (contents !== undefined) this.contents = contents
    this.boxes = Loader.loadKeyDict(data, 'boxes', (data) => new LayoutBox(data), this.boxes)
    const editable = Loader.loadOptionalBoolean(data, 'editable')
    if (editable !== undefined) this.editable = editable
    const existingLayout = Loader.loadOptionalClass(data, 'existingLayout', (data) => new Layout(data))
    if (existingLayout !== undefined) this.existingLayout = existingLayout
    this.transition = Loader.loadAndCast<SceneTransition>(data, 'transition', this.transition)
    this.castercss = Loader.loadClass(data, 'castercss', (data) => new CasterCSS(data), this.castercss)
    this.chatcss = Loader.loadClass(data, 'chatcss', (data) => new ChatCSS(data), this.chatcss)
    this.tweetcss = Loader.loadClass(data, 'tweetcss', (data) => new TwitterStyling(data), this.tweetcss)
    this.livechatcss = Loader.loadClass(data, 'livechatcss', (data) => new LiveChatCSS(data), this.livechatcss)
    this.simplecgcss = Loader.loadKeyDict(data, 'simplecgcss', (data: unknown) => data as SimplecgCSS, this.simplecgcss)
    if (Object.values(this.simplecgcss).length > 0 && Object.values(this.boxes).length > 0) {
      Object.keys(this.simplecgcss).forEach((id) => {
        const box = Object.values(this.boxes).find((box) => box.id === id)
        if (box) {
          const simpleCgStyling = BoxStylingSimpleCg.mapSimpleCgCssToBoxStyling(
            this.simplecgcss[box.id], this.boxes[box.id].styling
          )
          this.boxes[box.id].styling = simpleCgStyling
        }
      })
    }
    this.clips = Loader.loadAndCast<{ automute: boolean }>(data, 'clips', this.clips)
    this.team_id = Loader.loadString(data, 'team_id', this.team_id)
  }

  static load (data: unknown): Result<Layout, 'invalid_params'|'unknown'> {
    try {
      return Result.success(new Layout(data))
    } catch (error) {
      return Result.fromUnknownError('invalid_params', error)
    }
  }

  static create (params: Pick<Layout, 'name'|'team_id'>): Result<Layout, 'invalid_params'> {
    const layout: Layout = new Layout({
      name: params.name,
      info: '',
      boxes: {},
      transition: {
        type: 'none',
        duration: 0,
        actions: []
      },
      castercss: new CasterCSS(),
      chatcss: new ChatCSS(),
      tweetcss: new TwitterStyling(),
      livechatcss: new LiveChatCSS(),
      simplecgcss: {},
      clips: {
        automute: false
      },
      team_id: params.team_id
    })
    return Result.success(layout)
  }
}
